import { formatNameLastFirst } from '@strata/helper-functions';

import {
    getPayerDetails,
    getPrescriberInfo,
    getSupplierChildren,
    getSupplierDetails,
} from 'api/referenceAPI';
import { handleError } from 'services/Error';
import { AppThunk } from 'store';
import {
    setPayerDetails,
    setPrescriberDetails,
    setSupplierDetails,
} from 'store/reference/referenceSlice';

export const fetchPayerDetails =
    (payerId: string): AppThunk =>
    async (dispatch) => {
        try {
            const response = await getPayerDetails(payerId);
            const payer = {
                payerId: response.payer_id,
                payerName: response.payer_name,
                payerEmail: response.payer_email,
                payerFeatures: response.features,
            };
            dispatch(setPayerDetails({ payerId, payer }));
        } catch (err) {
            handleError('An error occurred retrieving payer details.', err as Error);
        }
    };

export const fetchPrescriberDetails =
    (prescriberNpi: string): AppThunk =>
    async (dispatch) => {
        try {
            const response = await getPrescriberInfo(prescriberNpi);
            const prescriber = {
                prescriberNpi,
                name: formatNameLastFirst(response.last_name, response.first_name),
                firstName: response.first_name,
                telephoneNumber: response.telephone_number,
                faxNumber: response.fax_number,
                address1: response.address_1,
                address2: response.address_2,
                city: response.city,
                state: response.state,
                postalCode: response.postal_code,
                organizationName: response.organization_name,
                practiceNpi: response.practice_npi,
            };
            dispatch(setPrescriberDetails({ prescriberNpi, prescriber }));
            return prescriber;
        } catch (err) {
            handleError('An error occurred retrieving prescriber details.', err as Error);
            return undefined;
        }
    };

export const fetchSupplierDetails =
    (supplierNpi: string): AppThunk =>
    async (dispatch) => {
        try {
            const [details, children] = await Promise.all([
                getSupplierDetails(supplierNpi),
                getSupplierChildren(supplierNpi),
            ]);
            const supplier = {
                name: details.organization_name,
                phone: details.telephone_number,
                fax: details.fax_number,
                email: details.email,
                address1: details.address_1,
                address2: details.address_2,
                city: details.city,
                state: details.state,
                zip: details.postal_code,
                npi: details.supplier_npi,
                networkStatus: details.network_status,
                taxonomyCodes:
                    details.extra_taxonomy_codes?.map((code) => ({
                        name: code.taxonomy_code,
                        secondaryData: code.taxonomy_description,
                    })) ?? [],
                childNpis: children?.npis ?? [],
                billingSupplierNpi: children?.billing_supplier_npi,
            };
            dispatch(setSupplierDetails({ supplierNpi, supplier }));
        } catch (err) {
            handleError('An error occurred retrieving supplier details.', err as Error);
        }
    };
